import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './CoursePlanYearComparison.css';

const CHANGE_LABELS = { added: 'Eklendi', removed: 'Kaldırıldı', changed: 'Güncellendi' };
const FIELD_LABELS = {
  donem: 'Dönem', course_name: 'Ders adı', course_type: 'Ders tipi', teorik: 'Teorik',
  uygulama: 'Uygulama', laboratuvar: 'Laboratuvar', kredi: 'Kredi', akts: 'AKTS',
};

const formatValue = (value) => (value === null || value === undefined || value === '' ? 'Belirtilmemiş' : String(value));

function ChangeImpact({ change, sourceYear, targetYear }) {
  const oldCourse = change.old_course;
  const newCourse = change.new_course;

  if (change.change_type === 'added') {
    return <p className="course-change-impact">Bu ders <b>{targetYear}</b> planına eklendi. {newCourse?.donem ? `${newCourse.donem}. dönemde yer alıyor.` : ''}</p>;
  }
  if (change.change_type === 'removed') {
    return <p className="course-change-impact">Bu ders <b>{sourceYear}</b> planından çıkarıldı. {oldCourse?.donem ? `${oldCourse.donem}. dönemde yer alıyordu.` : ''}</p>;
  }

  return (
    <dl className="course-change-diff" aria-label={`${change.course_code} için eski ve yeni değerler`}>
      {change.changed_fields.map((field) => (
        <div key={field}>
          <dt>{FIELD_LABELS[field] || field}</dt>
          <dd><span><b>{sourceYear}:</b> {formatValue(oldCourse?.[field])}</span><span><b>{targetYear}:</b> {formatValue(newCourse?.[field])}</span></dd>
        </div>
      ))}
    </dl>
  );
}

function CourseTable({ title, courses, tone, changes, emptyMessage }) {
  const changesByCode = new Map((changes || []).map((change) => [String(change.course_code).toUpperCase(), change.change_type]));
  return (
    <section className={`course-plan-table course-plan-table--${tone}`}>
      <div className="course-plan-table__heading"><h3>{title}</h3><span>{courses.length} ders</span></div>
      <div className="course-plan-table__scroll">
        <table>
          <thead><tr><th>Ders kodu</th><th>Ders adı</th><th>Tür</th><th>T</th><th>U</th><th>L</th><th>Kredi</th><th>AKTS</th></tr></thead>
          <tbody>
            {courses.length === 0 ? <tr><td colSpan="8" className="course-plan-table__empty">{emptyMessage || 'Bu dönem için ders kaydı bulunmuyor.'}</td></tr> : courses.map((course) => {
              const changeType = changesByCode.get(String(course.course_code).toUpperCase());
              return <tr key={`${course.course_code}-${course.id}`} className={changeType ? `course-plan-table__row--${changeType}` : ''}>
                <td><span>{course.course_code}</span>{changeType && <small>{CHANGE_LABELS[changeType]}</small>}</td><td>{course.course_name}</td><td>{course.course_type}</td>
                <td>{course.teorik}</td><td>{course.uygulama}</td><td>{course.laboratuvar}</td><td>{course.kredi}</td><td>{course.akts}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function CoursePlanYearComparison({ programId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reasonDrafts, setReasonDrafts] = useState({});
  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');

  const load = useCallback(async () => {
    if (!programId || !token) { setError('Karşılaştırma için program bilgisi veya yetki doğrulaması bulunamadı.'); setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/course-plan/year-comparison`, { headers: { Authorization: `Bearer ${token}` } });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Ders planı karşılaştırması yüklenemedi.');
      setData(payload);
      setReasonDrafts(Object.fromEntries((payload.changes || []).map((item) => [`${item.course_code}-${item.change_type}`, item.reason || ''])));
    } catch (requestError) { setData(null); setError(requestError.message || 'Ders planı karşılaştırması yüklenemedi.'); }
    finally { setLoading(false); }
  }, [programId, token]);

  useEffect(() => { load(); }, [load]);

  const saveReason = async (change) => {
    const key = `${change.course_code}-${change.change_type}`;
    const reason = String(reasonDrafts[key] || '').trim();
    if (!reason) { setError(`${change.course_code} için değişiklik gerekçesi girin.`); return; }
    setSavingKey(key); setSavedKey(''); setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/course-plan/year-comparison/reasons`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source_year: data.source_year.year, target_year: data.target_year.year, course_code: change.course_code, change_type: change.change_type, reason }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Gerekçe kaydedilemedi.');
      setData((previous) => ({ ...previous, changes: previous.changes.map((item) => (
        item.course_code === change.course_code && item.change_type === change.change_type ? { ...item, reason: payload.reason } : item
      )) }));
      setSavedKey(key);
    } catch (requestError) { setError(requestError.message || 'Gerekçe kaydedilemedi.'); }
    finally { setSavingKey(''); }
  };

  if (loading) return <div className="course-comparison-state">Ders planı karşılaştırması hazırlanıyor…</div>;
  if (error && !data) return <div className="course-comparison-state course-comparison-state--error"><p>{error}</p><button type="button" onClick={load}>Yeniden Dene</button></div>;
  if (!data) return null;
  const changeTotal = data.added_count + data.removed_count + data.changed_count;
  const periods = [...new Set([...(data.source_courses || []), ...(data.target_courses || [])].map((course) => Number(course.donem) || 0))]
    .sort((first, second) => first - second);
  const sourceChanges = (data.changes || []).filter((change) => change.change_type !== 'added');
  const targetChanges = (data.changes || []).filter((change) => change.change_type !== 'removed');
  const completedReasonCount = (data.changes || []).filter((change) => String(change.reason || '').trim()).length;
  const missingReasonCount = changeTotal - completedReasonCount;

  return (
    <div className="course-comparison">
      {error && <div className="course-comparison-error no-print">{error}</div>}
      <section className="course-comparison-hero">
        <div><span>DENETİM KANITI · DERS PLANI KARŞILAŞTIRMASI</span><h2>Ders planında ne değişti?</h2><p><b>{data.source_year.label}</b> planını <b>{data.target_year.label}</b> planıyla karşılaştırın; ardından her değişiklik için gerekçeyi kaydedin.</p></div>
        <strong>{changeTotal}<small>tespit edilen değişiklik</small></strong>
      </section>
      <div className="course-comparison-meta"><span><b>Program</b>{data.program.name}</span><span><b>Kaynak plan</b>{data.source_year.label}</span><span><b>Yeni plan</b>{data.target_year.label}</span></div>
      <div className="course-comparison-actions no-print"><button type="button" onClick={load}>Karşılaştırmayı Yenile</button><button type="button" onClick={() => window.print()}>PDF Raporunu Yazdır</button></div>

      <section className="course-comparison-overview" aria-labelledby="comparison-overview-title">
        <div className="course-comparison-overview__heading"><div><span>KISA ÖZET</span><h3 id="comparison-overview-title">Değişiklik özeti</h3></div><a href="#course-change-report">Gerekçelere git</a></div>
        <div className="course-comparison-counts">
          <a className="course-comparison-count course-comparison-count--added" href="#course-change-report"><strong>{data.added_count}</strong><span>Eklendi</span><small>Yeni planda yer alan dersler</small></a>
          <a className="course-comparison-count course-comparison-count--removed" href="#course-change-report"><strong>{data.removed_count}</strong><span>Kaldırıldı</span><small>Yeni planda yer almayan dersler</small></a>
          <a className="course-comparison-count course-comparison-count--changed" href="#course-change-report"><strong>{data.changed_count}</strong><span>Güncellendi</span><small>Bilgileri değişen dersler</small></a>
        </div>
        <div className="course-comparison-reading-guide"><b>Nasıl okunur?</b><span><i className="course-comparison-legend course-comparison-legend--old" />Solda önceki plan, sağda yeni plan bulunur.</span><span><i className="course-comparison-legend course-comparison-legend--added" />Eklendi</span><span><i className="course-comparison-legend course-comparison-legend--removed" />Kaldırıldı</span><span><i className="course-comparison-legend course-comparison-legend--changed" />Güncellendi</span></div>
      </section>

      {data.missing_target_periods?.length > 0 && <aside className="course-comparison-data-note" aria-label="Eksik ders planı verisi uyarısı"><b>Karşılaştırma notu:</b> Yeni planın {data.missing_target_periods.join(', ')}. dönem verisi henüz aktarılmamış. Bu dönemlerdeki dersler kaldırılmış olarak sayılmaz.</aside>}

      <section className="course-comparison-tables" aria-labelledby="course-plan-tables-title">
        <div className="course-comparison-section-heading"><div><span>AYRINTILI KARŞILAŞTIRMA</span><h3 id="course-plan-tables-title">Dönemlere göre eski ve yeni plan</h3></div><p>Renkli satırların ayrıntısını aşağıdaki “Gerekçeli değişiklikler” bölümünde görebilirsiniz.</p></div>
        {periods.map((period) => (
          <section className="course-comparison-period" key={period}>
            <h3>{period}. Dönem Ders Planı Karşılaştırması</h3>
            <div className="course-comparison-period__tables">
              <CourseTable title={`${data.source_year.label} Eski Plan`} courses={(data.source_courses || []).filter((course) => Number(course.donem) === period)} tone="old" changes={sourceChanges} />
              <CourseTable
                title={`${data.target_year.label} Yeni Plan`}
                courses={(data.target_courses || []).filter((course) => Number(course.donem) === period)}
                tone="new"
                changes={targetChanges}
                emptyMessage={(data.missing_target_periods || []).includes(period)
                  ? 'Yeni ders planında bu döneme ait kayıt henüz aktarılmadı veya dersler henüz açılmadı; bu nedenle dersler kaldırılmış sayılmaz.'
                  : undefined}
              />
            </div>
          </section>
        ))}
      </section>

      <section className="course-change-report" id="course-change-report" aria-labelledby="course-change-report-title">
        <div className="course-change-report__heading"><div><span>KANITIN TAMAMLANMASI</span><h3 id="course-change-report-title">Gerekçeli ders planı değişiklikleri</h3><p>Her değişiklik için gerekçe girip kaydederek denetim kanıtını tamamlayın.</p></div><div className={`course-change-report__progress${missingReasonCount === 0 ? ' course-change-report__progress--complete' : ''}`}><strong>{completedReasonCount}/{changeTotal}</strong><span>{missingReasonCount === 0 ? 'Tüm gerekçeler kaydedildi' : `${missingReasonCount} gerekçe bekliyor`}</span></div></div>
        {data.changes.length === 0 ? <p className="course-change-report__empty">İki ders planı arasında değişiklik bulunmuyor.</p> : (
          <ol className="course-change-list">
            {data.changes.map((change) => {
              const key = `${change.course_code}-${change.change_type}`;
              const course = change.new_course || change.old_course;
              return <li key={key} className={`course-change-list__item course-change-list__item--${change.change_type}`}>
                <div className="course-change-list__summary"><span>{CHANGE_LABELS[change.change_type]}</span><strong>{change.course_code}</strong><p>{course?.course_name || 'Ders kaydı'}</p><ChangeImpact change={change} sourceYear={data.source_year.label} targetYear={data.target_year.label} /></div>
                <div className="course-change-list__reason no-print"><label htmlFor={`course-reason-${key}`}>Bu değişiklik neden yapıldı?</label><textarea id={`course-reason-${key}`} rows="3" value={reasonDrafts[key] || ''} onChange={(event) => setReasonDrafts((previous) => ({ ...previous, [key]: event.target.value }))} placeholder="Örn. sektör ihtiyacı, müfredat güncellemesi veya kurul kararı." /><div className="course-change-list__reason-action"><button type="button" disabled={savingKey === key} onClick={() => saveReason(change)}>{savingKey === key ? 'Kaydediliyor…' : 'Gerekçeyi Kaydet'}</button>{savedKey === key && <span role="status">Kaydedildi</span>}</div></div>
                <p className="course-change-list__print print-only"><b>Değişiklik gerekçesi:</b> {change.reason || 'Gerekçe girilmedi.'}</p>
              </li>;
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
